"use client";
import ProductCard from "@/components/ProductCard";


import { useEffect, useRef, useState } from "react";
import classes from "./TrendingProducts.module.css";

import "swiper/css";
import "swiper/css/navigation";
import { Autoplay, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import {
  ArrowSquareDownOutlineIcon,
  ArrowUpSquareOutlineIcon,
} from "@/components/Icons";
import ShowMoreButton from "@/components/buttons/ShowMoreButton";
import Link from "next/link";

import { useLogicalProductsManual } from "@/hooks/useLogicalProducts";
import { getSectionStyle } from "@/utils";
import { getPageRoute, getS3ImageUrl } from "@/utils/routes";
import Image from "next/image";
import { ProductColumnSkeleton, ProductRowSkeleton } from "../skeleton";

const TrendingProducts = ({
  widget,
  initialLeftProducts,
  initialMainProducts,
}: {
  widget?: Widget;
  initialLeftProducts?: ProductItem[];
  initialMainProducts?: ProductItem[];
}) => {
  const mainRef = useRef(null);
  const pcRef = useRef(null);

  const {
    products: leftProducts,
    loading: leftLoading,
    error: leftError,
  } = useLogicalProductsManual({
    custom: widget?.data?.leftProducts?.custom,
    logical: widget?.data?.leftProducts?.logical,
    ref: mainRef,
    initialProducts: initialLeftProducts,
  });

  const {
    products: mainProducts,
    loading: mainLoading,
    error: mainError,
  } = useLogicalProductsManual({
    custom: widget?.data?.mainProducts?.custom,
    logical: widget?.data?.mainProducts?.logical,
    ref: mainRef,
    initialProducts: initialMainProducts,
  });

  const [containerHeight, setContainerHeight] = useState(0);

  const featuredImage = widget?.data?.featuredImage;

  useEffect(() => {
    const calcHeight = () => {
      if (pcRef.current) {
        const target = pcRef.current as HTMLDivElement;
        const height = target.clientHeight;
        setContainerHeight(height);
      }
    };

    calcHeight();

    setTimeout(calcHeight, 600);
    setTimeout(calcHeight, 3000);

    window.addEventListener("resize", calcHeight);

    return () => {
      window.removeEventListener("resize", calcHeight);
    };
  }, [pcRef, mainProducts, leftProducts, leftLoading, mainLoading]);

  const settings = widget?.data?.settings || {};

  // if (productsData?.length === 0 && !loading) return null;
  // if (!mainProducts?.length && !mainLoading) return null;

  return (
    <section
      ref={mainRef}
      className={classes.TrendingProducts}
      style={getSectionStyle(settings)}
    >
      <div className="container lg:flex gap-2">
        {(leftProducts?.length > 0 || leftLoading) && (
          <div className={classes.left}>
            <div className={classes.header}>
              <h3 className={classes.title}>Latest Products</h3>

              <div className={classes.buttons}>
                <button
                  className={classes.button}
                  id="swiper-button-next-trending-321"
                >
                  <ArrowUpSquareOutlineIcon className={classes.icon} />
                </button>

                <button
                  className={classes.button}
                  id="swiper-button-prev-trending-321"
                >
                  <ArrowSquareDownOutlineIcon className={classes.icon} />
                </button>
              </div>
            </div>

            {leftLoading && <ProductColumnSkeleton />}

            {leftProducts?.length > 0 && (
              <div className={classes.slider}>
                <Swiper
                  spaceBetween={0}
                  slidesPerView={3}
                  slidesPerGroup={3}
                  breakpoints={{
                    1024: {
                      slidesPerView: 8,
                    },
                    1280: {
                      slidesPerView: 5,
                    },
                  }}
                  navigation={{
                    nextEl: "#swiper-button-next-trending-321",
                    prevEl: "#swiper-button-prev-trending-321",
                  }}
                  autoplay={{
                    delay: 2500,
                  }}
                  speed={600}
                  modules={[Navigation, Autoplay]}
                  className={classes.swiper}
                  direction="vertical"
                  style={{
                    ...(containerHeight && {
                      height: containerHeight + "px",
                      maxHeight: "100vh",
                    }),
                  }}
                >
                  {leftProducts?.map((product, i) => (
                    <SwiperSlide key={`trend-swiper-${product.slug || product.id}`}>
                      <ProductCard productItem={product} key={product.slug || product.id} type="xs" />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            )}
          </div>
        )}

        <div className={classes.right}>
          <div className={classes.header}>
            <h3 className={classes.title}>Trending Products</h3>

            <div className={classes.buttons}>
              <Link href={"#"}>
                <ShowMoreButton type="all" />
              </Link>
            </div>
          </div>

          <div ref={pcRef} className={classes.body}>
            <div className={classes.products}>
              {mainProducts?.slice(0, 4).map((product, i) => (
                <ProductCard key={`trend-lg-${product.slug || product.id}`} productItem={product} type="lg" />
              ))}
            </div>

            {mainLoading && <ProductRowSkeleton />}

            <div className={classes.banner}>
              <Link
                href={getPageRoute(
                  featuredImage?.link?.urlType || "custom",
                  featuredImage?.link?.url || ""
                )}
                className={!featuredImage?.link ? "no-link" : ""}
              >
                <Image
                  src={getS3ImageUrl(featuredImage?.image || "")}
                  alt="banner"
                  layout="responsive"
                  width={985}
                  height={145}
                  unoptimized={true}
                  style={{
                    aspectRatio: 985 / 145,
                    objectFit: "cover",
                  }}
                />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrendingProducts;
